/**
 * AI Configuration - Provider Tab
 *
 * T015: Provider dropdown, API key, Test Connection, Monthly Spend Limit,
 * Quality Level, Cache AI Responses, Batch Processing, Save Changes
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, CheckCircle, AlertCircle, Eye, EyeOff, Save } from 'lucide-react';
import { useAiSettings, useSaveAiSettings, useProviderModelMappings } from '../../../hooks/useAiSettings';
import { aiConfigurationService } from '../../../services/ai-configuration-service';
import { setNavigationGuardState } from '../useNavigationGuardState';
import type { AiProvider, QualityLevel, SaveAiSettingsInput } from '../../../types/ai-configuration';

const PROVIDERS: { value: AiProvider; label: string }[] = [
  { value: 'none', label: 'None (disabled)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google', label: 'Google' },
  { value: 'grok', label: 'Grok (xAI)' },
];

const QUALITY_LEVELS: { value: QualityLevel; label: string }[] = [
  { value: 'fast', label: 'Fast' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'accurate', label: 'Accurate' },
];

export function ProviderTab(): React.ReactElement {
  const { data: settings, isLoading, error } = useAiSettings();
  const saveMutation = useSaveAiSettings();
  const [formData, setFormData] = useState<Partial<SaveAiSettingsInput>>({});
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (settings) {
      setFormData({
        provider: settings.provider,
        monthlySpendLimitCents: settings.monthlySpendLimitCents,
        qualityLevel: settings.qualityLevel,
      });
    }
  }, [settings]);

  const isDirty = useMemo(() => {
    if (!settings) return false;
    return (
      formData.provider !== settings.provider ||
      formData.monthlySpendLimitCents !== settings.monthlySpendLimitCents ||
      formData.qualityLevel !== settings.qualityLevel ||
      (apiKeyInput.trim().length > 0)
    );
  }, [settings, formData, apiKeyInput]);

  useEffect(() => {
    setNavigationGuardState(isDirty, () => {});
    return () => setNavigationGuardState(false, null);
  }, [isDirty]);

  const provider = formData.provider ?? settings?.provider ?? 'openai';
  const isProviderEnabled = provider !== 'none';
  const { data: modelMappings } = useProviderModelMappings(
    isProviderEnabled ? provider : ''
  );

  const handleTestConnection = useCallback(async () => {
    const key = apiKeyInput.trim();
    const hasStoredKey = !!settings?.apiKeyMasked;
    if (!key && !hasStoredKey) {
      setTestMessage('Enter your API key to test the connection');
      setTestStatus('error');
      return;
    }
    setTestStatus('testing');
    setTestMessage('');
    const result = await aiConfigurationService.testConnection(
      undefined,
      key,
      provider as AiProvider,
      !key && hasStoredKey
    );
    if (result.error) {
      setTestStatus('error');
      setTestMessage(result.error.message);
    } else {
      setTestStatus('success');
      setTestMessage('Connection successful');
    }
  }, [formData.provider, settings?.provider, settings?.apiKeyMasked, apiKeyInput, provider, settings]);

  const handleSave = useCallback(async () => {
    if (!isDirty) return;
    setSaveStatus('saving');
    try {
      const input: SaveAiSettingsInput = { ...formData };
      if (apiKeyInput.trim()) input.apiKey = apiKeyInput.trim();
      await saveMutation.mutateAsync(input);
      setApiKeyInput('');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  }, [isDirty, formData, apiKeyInput, saveMutation]);

  const handleClearApiKey = useCallback(async () => {
    setSaveStatus('saving');
    try {
      await saveMutation.mutateAsync({ ...formData, clearApiKey: true });
      setApiKeyInput('');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  }, [formData, saveMutation]);

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <p className="text-red-800 dark:text-red-200">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-content space-y-6 pt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              AI Provider
            </label>
            <select
              value={formData.provider ?? settings.provider}
              onChange={(e) =>
                setFormData((p) => ({ ...p, provider: e.target.value as AiProvider }))
              }
              className="input input-select w-full max-w-xs"
              aria-label="AI Provider"
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {!isProviderEnabled && settings.apiKeyMasked && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                AI is disabled. You have a stored API key. Clear it if you no longer need it.
              </p>
              <button
                type="button"
                onClick={handleClearApiKey}
                disabled={saveStatus === 'saving'}
                className="btn btn-secondary h-10 px-4"
                aria-label="Clear stored API key"
              >
                Clear stored API key
              </button>
            </div>
          )}

          {isProviderEnabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  API Key
                </label>
                <div className="flex flex-wrap gap-2 items-center max-w-md">
                  <div className="flex gap-2 flex-1 min-w-0">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                      placeholder={settings.apiKeyMasked ?? 'Enter your API key'}
                      className="input flex-1"
                      aria-label="API Key"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="btn btn-secondary shrink-0 h-10 w-10 min-w-[2.5rem]"
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {settings.apiKeyMasked && (
                    <button
                      type="button"
                      onClick={handleClearApiKey}
                      disabled={saveStatus === 'saving'}
                      className="btn btn-secondary h-10 px-4"
                      aria-label="Clear API key"
                    >
                      Clear API key
                    </button>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testStatus === 'testing'}
                  className="btn btn-secondary h-10 px-4"
                  aria-label="Test Connection"
                  aria-busy={testStatus === 'testing'}
                >
                  {testStatus === 'testing' ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    'Test Connection'
                  )}
                </button>
                {testMessage && (
                  <p
                    role="status"
                    aria-live="polite"
                    className={`mt-2 text-sm ${
                      testStatus === 'success'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {testMessage}
                  </p>
                )}
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Monthly Spend Limit ($)
            </label>
            <input
              type="number"
              min={0}
              value={formData.monthlySpendLimitCents != null ? formData.monthlySpendLimitCents / 100 : ''}
              onChange={(e) => {
                const v = e.target.value;
                setFormData((p) => ({
                  ...p,
                  monthlySpendLimitCents: v === '' ? null : Math.round(parseFloat(v) * 100),
                }));
              }}
              placeholder="No limit"
              className="input w-full max-w-xs"
              aria-label="Monthly Spend Limit in dollars"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quality Level
            </label>
            <select
              value={formData.qualityLevel ?? settings.qualityLevel}
              onChange={(e) =>
                setFormData((p) => ({ ...p, qualityLevel: e.target.value as QualityLevel }))
              }
              className="input input-select w-full max-w-xs"
              aria-label="Quality Level"
            >
              {QUALITY_LEVELS.map((q) => {
                const modelId = modelMappings?.[q.value];
                const label = modelId
                  ? `${q.label} (${modelId})`
                  : q.label;
                return (
                  <option key={q.value} value={q.value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saveStatus === 'saving'}
          data-loading={saveStatus === 'saving' ? 'true' : undefined}
          className="btn-primary btn-md flex items-center gap-2"
        >
          {saveStatus === 'saving' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {saveStatus === 'saved' && <CheckCircle className="h-4 w-4" aria-hidden="true" />}
          {saveStatus === 'error' && <AlertCircle className="h-4 w-4" aria-hidden="true" />}
          {saveStatus === 'idle' && <Save className="h-4 w-4" aria-hidden="true" />}
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
